import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { FxAlert, FxButton, FxIcon, FxSpinner } from '@/components/fx'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { ApiError, documentsApi } from '@/lib/api'
import type { EvaluationDocument, SinglePage } from '@/types/domain'
import { pageDisplayName } from '@/types/domain'

import { AlignCanvas } from './AlignCanvas'
import { type Quad, initialQuad, quadProblem, toApiCorners } from './corners'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** The analysis call, injectable so the dialog can be driven in a test
 *  without a gateway. Same shape as `documentsApi.startAnalysis`. */
export type StartAlignedAnalysis = (
  templateId: string,
  documentIds: string[],
  options: { forcePage?: number; corners?: number[][] },
) => Promise<string>

export interface AlignDialogProps {
  templateId: string
  /** The scan to align. `imagePath` is the file as uploaded — the frame the
   *  corners are fractions of. Never the analysed or enhanced page: those are
   *  already warped into the template's frame, which is the frame this screen
   *  exists to establish. */
  document: Pick<
    EvaluationDocument,
    'id' | 'title' | 'imagePath' | 'matchedPage' | 'matchMetrics'
  >
  /** The template's pages, i.e. its layouts (`template.pageList`). */
  layouts: SinglePage[]
  onClose: () => void
  /** The job the gateway queued. The caller does not wait for it — the
   *  document goes back to "in Verarbeitung" and the cockpit polls. */
  onStarted?: (jobId: string) => void
  startAnalysis?: StartAlignedAnalysis
}

/**
 * "Ausrichtung korrigieren" — the rescue for a scan the matcher could not
 * place.
 *
 * A person drags four handles onto the corners of the form as it sits on the
 * photograph; the job then re-runs with those corners instead of SIFT
 * (`matching.manual_match`). Two things make this screen different from every
 * other one in the product, and both are stated on it in words:
 *
 *  - it **overrides** the matcher. The warp's similarity is still measured and
 *    stored, but it can no longer reject the match — the person has decided.
 *  - nothing here is a suggestion. The handles start at fixed insets; no
 *    machine proposed this quadrilateral, and the copy must not imply one did.
 *
 * It is a component, not a route: the cockpit's Problemfälle and the review
 * screen both open it over themselves (`?dialog=align&doc=<id>`).
 */
export function AlignDialog({
  templateId,
  document: doc,
  layouts,
  onClose,
  onStarted,
  startAnalysis = documentsApi.startAnalysis,
}: AlignDialogProps) {
  const titleId = useId()
  const layoutId = useId()
  const dialog = useRef<HTMLDivElement | null>(null)

  const [quad, setQuad] = useState<Quad>(() => initialQuad())
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const [imageFailed, setImageFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // The layout the document was matched to is the best guess available, and
  // on a noMatch document there is none — then the first layout, which the
  // person can change. `matchMetrics.forcedPage` remembers an earlier manual
  // override, which is a better guess than "the first one".
  const [page, setPage] = useState<number | null>(
    () =>
      doc.matchedPage ??
      doc.matchMetrics?.forcedPage ??
      (layouts.length > 0 ? layouts[0].page : null),
  )

  const { url, error: imageError } = useBlobUrl(doc.imagePath)
  const problem = useMemo(() => quadProblem(quad), [quad])

  // Escape closes and Tab stays inside. A modal that leaks focus to the page
  // it is covering cannot be used with a keyboard or a screen reader.
  useEffect(() => {
    const node = dialog.current
    if (!node) return
    const previous = window.document.activeElement as HTMLElement | null
    node.querySelector<HTMLElement>(FOCUSABLE)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (!event.shiftKey && window.document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && window.document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)
    return () => {
      node.removeEventListener('keydown', onKeyDown)
      previous?.focus?.()
    }
  }, [onClose])

  const loadFailed = imageFailed || imageError != null || !doc.imagePath
  const ready = !loadFailed && natural != null && page != null && problem == null && !busy

  const submit = async () => {
    if (!ready || page == null) return
    setBusy(true)
    setSubmitError(null)
    try {
      // Fractions, in the fixed order, exactly as they stand. Anything that
      // converted them to pixels or sorted them geometrically here would be
      // invisible on screen and wrong on the worker.
      const jobId = await startAnalysis(templateId, [doc.id], {
        forcePage: page,
        corners: toApiCorners(quad),
      })
      onStarted?.(jobId)
      onClose()
    } catch (cause) {
      setSubmitError(analysisErrorText(cause))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto p-4 sm:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      {/* The design system dims the page with a 100vmax spread shadow rather
          than a tinted element, so there is no scrim colour to get wrong. */}
      <div aria-hidden className="pointer-events-none fixed left-1/2 top-1/2 h-0 w-0 shadow-scrim" />

      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative my-auto flex h-fit w-[760px] max-w-full flex-col gap-4 rounded-xl bg-surface p-10 shadow-overlay"
      >
        <button
          type="button"
          aria-label="Schließen"
          onClick={onClose}
          className="absolute right-10 top-10 flex rounded-sm text-text"
        >
          <FxIcon name="close" size={30} />
        </button>

        <h2 id={titleId} className="m-0 pr-10 text-dialog-title">
          Ausrichtung korrigieren
        </h2>
        <p className="m-0">
          Ziehen Sie die vier Punkte auf die Ecken des Formulars in „{doc.title}“. Das Dokument
          wird dann mit dieser Ausrichtung neu analysiert.
        </p>
        {/* Said plainly, because it is the whole point of the screen: from
            here on the machine's own judgement about this page is out of the
            way. */}
        <p className="m-0 text-small text-text-muted">
          Ihre Markierung ersetzt die automatische Erkennung. Die Seite wird so zugeordnet, wie Sie
          sie markieren — auch wenn die Ähnlichkeit zur Vorlage gering ist.
        </p>

        {layouts.length > 1 && (
          <div className="flex items-center gap-3">
            <label htmlFor={layoutId} className="text-body-strong">
              Layout
            </label>
            <select
              id={layoutId}
              value={page ?? ''}
              onChange={(event) => setPage(Number(event.target.value))}
              className="h-control w-[200px] rounded-md bg-ground px-3 text-body text-text shadow-raised"
            >
              {layouts.map((layout) => (
                <option key={layout.page} value={layout.page}>
                  {pageDisplayName(layout)}
                </option>
              ))}
            </select>
          </div>
        )}

        {loadFailed ? (
          <FxAlert tone="danger">Der Scan konnte nicht geladen werden.</FxAlert>
        ) : !url ? (
          <div className="grid h-[240px] place-items-center rounded-sm bg-ground">
            <FxSpinner label="Scan wird geladen …" />
          </div>
        ) : (
          <AlignCanvas
            url={url}
            natural={natural}
            quad={quad}
            onQuadChange={setQuad}
            activeIndex={activeIndex}
            onActiveIndexChange={setActiveIndex}
            onNatural={setNatural}
            onImageError={() => setImageFailed(true)}
            documentTitle={doc.title}
          />
        )}

        {problem && <FxAlert tone="warning">{problem.message}</FxAlert>}
        {submitError && <FxAlert tone="danger">{submitError}</FxAlert>}

        <div className="flex flex-wrap items-center gap-4">
          <FxButton
            variant="outlined"
            onClick={() => {
              setQuad(initialQuad())
              setActiveIndex(null)
            }}
          >
            Zurücksetzen
          </FxButton>
          <span className="grow" />
          <FxButton variant="tonal" onClick={onClose} disabled={busy}>
            Abbrechen
          </FxButton>
          <FxButton variant="primary" onClick={submit} disabled={!ready}>
            {busy ? 'Wird gestartet …' : 'Neu analysieren'}
          </FxButton>
        </div>
      </div>
    </div>
  )
}

/**
 * What to put in front of a person when the gateway refuses.
 *
 * `invalid_corners` covers several rules at once (four points, each in
 * [0, 1], one document, a layout on a multi-page template) and arrives as
 * English prose from the queue. Everything this dialog can produce is already
 * refused locally, so reaching it means the two validations disagree — which
 * is worth naming as "bitte melden" rather than dressing up.
 */
function analysisErrorText(cause: unknown): string {
  const api = ApiError.from(cause)
  if (api.code === 'invalid_corners') {
    return 'Die Markierung wurde abgelehnt. Bitte setzen Sie die Punkte zurück und markieren Sie das Formular erneut.'
  }
  if (api.status === 403) {
    return 'Für diese Vorlage fehlt Ihnen die Berechtigung zum Analysieren.'
  }
  return 'Die Analyse konnte nicht gestartet werden. Bitte versuchen Sie es erneut.'
}
